import React from "react";
import { Link } from "react-router-dom";
import { Avatar, StatusPill } from "./Atoms";
import { Link as LinkIcon, Eye, DownloadSimple, CheckCircle, XCircle } from "@phosphor-icons/react";

/**
 * Shared "Creators assigned to this task" table.
 * Used on Brand Project Detail (nested) AND Brand Task Detail page.
 * Rows are guaranteed single-line: Pending uses a row of icon buttons (no stacked layout).
 */
export default function CreatorSubsTable({ rows, feedback, setFeedback, onAct, indent = false }) {
  const pad = indent ? "pl-12" : "pl-3";
  if (rows.length === 0) {
    return <div className="p-4 text-sm text-muted italic">No creators assigned to this task yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="creators-subs-table">
        <thead className="text-[10px] uppercase text-muted font-bold tracking-wider border-t border-white/5">
          <tr>
            <th className={`text-left p-2.5 ${pad}`}>Creator</th>
            <th className="text-left p-2.5">Proof Link</th>
            <th className="text-left p-2.5">Submitted File</th>
            <th className="text-left p-2.5 whitespace-nowrap">Status</th>
            <th className="text-left p-2.5 whitespace-nowrap">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.flatMap(({ amb, subs }) => {
            if (subs.length === 0) {
              return [(
                <tr key={`${amb.id}-none`} className="border-t border-white/5 align-middle h-12" data-testid={`task-amb-row-${amb.id}`}>
                  <td className={`p-2.5 ${pad}`}>
                    <Link to={`/client/ambassadors/${amb.id}`} className="flex items-center gap-2 hover:opacity-80" data-testid={`task-amb-link-${amb.id}`}>
                      <Avatar src={amb.photo} name={amb.name} size={28} />
                      <span className="text-white font-semibold hover:text-[#22D3EE]">{amb.name}</span>
                    </Link>
                  </td>
                  <td className="p-2.5 text-muted">—</td>
                  <td className="p-2.5 text-muted">—</td>
                  <td className="p-2.5 whitespace-nowrap"><StatusPill status="Not started" /></td>
                  <td className="p-2.5 text-muted italic whitespace-nowrap">No action</td>
                </tr>
              )];
            }
            return subs.map((s, i) => (
              <tr key={s.id} className="border-t border-white/5 align-middle h-12" data-testid={`task-amb-sub-${s.id}`}>
                <td className={`p-2.5 ${pad}`}>
                  {i === 0 ? (
                    <Link to={`/client/ambassadors/${amb.id}`} className="flex items-center gap-2 hover:opacity-80" data-testid={`task-amb-link-${amb.id}`}>
                      <Avatar src={amb.photo} name={amb.name} size={28} />
                      <span className="text-white font-semibold hover:text-[#22D3EE]">{amb.name}</span>
                    </Link>
                  ) : <span className="text-muted">↳</span>}
                </td>
                <td className="p-2.5">
                  {s.link ? <a href={s.link} target="_blank" rel="noreferrer" className="text-[#22D3EE] hover:underline flex items-center gap-1 whitespace-nowrap"><LinkIcon size={12} /> Open</a> : <span className="text-muted">—</span>}
                </td>
                <td className="p-2.5">
                  {s.file_url ? (
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer" className="text-[#22D3EE] hover:underline flex items-center gap-1" data-testid={`view-file-${s.id}`}><Eye size={12} weight="bold" /> View</a>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} download className="text-[#A3E635] hover:underline flex items-center gap-1" data-testid={`download-file-${s.id}`}><DownloadSimple size={12} weight="bold" /> Download</a>
                    </div>
                  ) : <span className="text-muted">—</span>}
                </td>
                <td className="p-2.5 whitespace-nowrap"><StatusPill status={s.status} /></td>
                <td className="p-2.5">
                  {s.status === "Pending" ? (
                    <div className="flex items-center gap-1.5 min-w-[240px]">
                      <input value={feedback[s.id] || ""} onChange={e => setFeedback({...feedback, [s.id]:e.target.value})} placeholder="Feedback (optional)" className="input-base text-xs py-1 flex-1" data-testid={`sub-feedback-${s.id}`} />
                      <button onClick={() => onAct(s.id, "approve")} className="btn-ghost text-[#A3E635] text-xs flex items-center gap-1 px-2 py-1 whitespace-nowrap" data-testid={`sub-approve-${s.id}`}><CheckCircle size={12} weight="bold" /></button>
                      <button onClick={() => onAct(s.id, "reject")} className="btn-ghost text-[#EC4899] text-xs flex items-center gap-1 px-2 py-1 whitespace-nowrap" data-testid={`sub-reject-${s.id}`}><XCircle size={12} weight="bold" /></button>
                    </div>
                  ) : <span className="text-muted whitespace-nowrap">—</span>}
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}
